import { nextTick } from 'process';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { ComponentCanDeactivate } from '@app/core/guards';
import { AuthService, AuthenticationInfo, FeedbackService } from '@app/core/services';
import { PurchaseOrderSetupCopyModel, PurchaseOrderSetupModel, PurchaseOrderSetupSiteModel, WrappedUserInfoModel } from '@app/models/system-settings';
import { ModalComponent } from '@app/shared/components';
import { ButtonBarComponent } from '@app/shared/components/button-bar/button-bar.component';
import { FileUploadComponent } from '@app/shared/components/file-upload/file-upload.component';
import { UploadedFileModel } from '@app/shared/components/file-upload/models/uploaded-file.model';
import { TitleComponent } from '@app/shared/components/title/title.component';
import { NgForTrackByIdDirective, NgForTrackByItemDirective } from '@app/shared/directives/ng-for-track-by-id.directive';
import { NumbersOnlyDirective } from '@app/shared/directives/numbers-only.directive';
import { PurchaseOrderSetupService } from './services/purchase-order-setup.service';

@Component({
	selector: 'sl-purchase-order-setup',
	templateUrl: './purchase-order-setup.component.html',
	styleUrls: ['./purchase-order-setup.component.less'],
	standalone: true,
	imports: [
		TitleComponent,
		NgIf,
		MatFormFieldModule,
		MatSelectModule,
		FormsModule,
		NgFor,
		NgForTrackByIdDirective,
		MatOptionModule,
		MatCardModule,
		MatCheckboxModule,
		MatInputModule,
		NumbersOnlyDirective,
		NgForTrackByItemDirective,
		MatAutocompleteModule,
		FileUploadComponent,
		MatTabsModule,
		MatButtonModule,
		ButtonBarComponent,
		AsyncPipe,
		MatTooltipModule,
	],
})
export class PurchaseOrderSetupComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
	@ViewChild('poSetupForm') poSetupForm: NgForm;

	copies = [1, 2, 3];
	entity: PurchaseOrderSetupModel;
	isReadOnly = false;
	sites: PurchaseOrderSetupSiteModel[] = [];
	siteId: number = null;
	userEmails: string[] = [];
	filteredUserEmails: Observable<string[]>;

	private readonly destroy$ = new Subject();
	private originalEntity: string;

	constructor(
		private readonly authService: AuthService,
		private readonly service: PurchaseOrderSetupService,
		private readonly dialog: MatDialog,
		private readonly feedbackService: FeedbackService,
	) {}

	@HostListener('window:beforeunload')
	canDeactivate = (): boolean => !this.poSetupForm.dirty;

	ngOnInit(): void {
		this.authService.authentication$.pipe(takeUntil(this.destroy$)).subscribe((authentication: AuthenticationInfo) => {
			if (!authentication) {
				return;
			}

			if (authentication.siteAccounting) {
				this.loadSites();
			} else {
				this.loadSetup();
			}

			this.loadUserEmails();
		});

		this.isReadOnly = this.authService.isReadOnly('system', 'SitePOSetup', 'system');
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	onSiteIdChanged(): void {
		this.loadSetup();
	}

	numberOfCopiesChanged(): void {
		const existingCopies = this.entity.SitePOSetupCopies?.length || 0;
		if (this.entity.NumberOfCopies > existingCopies) {
			for (let i = existingCopies + 1; i <= this.entity.NumberOfCopies; i++) {
				this.entity.SitePOSetupCopies.push({ CopyNumber: i } as PurchaseOrderSetupCopyModel);
			}
		} else {
			this.entity.SitePOSetupCopies = this.entity.SitePOSetupCopies.slice(0, this.entity.NumberOfCopies);
		}
	}

	logoChangeEvent(uploadedFile: UploadedFileModel): void {
		this.entity.Logo = uploadedFile?.fileBase64Encoded;
		this.poSetupForm.form.markAsDirty();
	}

	termsChangeEvent(uploadedFile: UploadedFileModel): void {
		this.entity.TermsBinary = uploadedFile?.fileBase64Encoded;
		this.entity.TermsFileType = uploadedFile?.fileName;
		this.poSetupForm.form.markAsDirty();
	}

	leftSignatureChangeEvent(copy: PurchaseOrderSetupCopyModel, uploadedFile: UploadedFileModel): void {
		copy.SigLeftImg = uploadedFile?.fileBase64Encoded;
		this.poSetupForm.form.markAsDirty();
	}

	rightSignatureChangeEvent(copy: PurchaseOrderSetupCopyModel, uploadedFile: UploadedFileModel): void {
		copy.SigRightImg = uploadedFile?.fileBase64Encoded;
		this.poSetupForm.form.markAsDirty();
	}

	cancelChanges(): void {
		this.entity = JSON.parse(this.originalEntity) as PurchaseOrderSetupModel;
		this.poSetupForm.form.markAsPristine();
		this.poSetupForm.form.markAsUntouched();
	}

	save(): void {
		this.service.saveSiteSetup(this.entity, this.siteId).subscribe(() => {
			this.originalEntity = JSON.stringify(this.entity);
			this.poSetupForm.form.markAsPristine();
			this.poSetupForm.form.markAsUntouched();
			this.feedbackService.success('saved', 'Purchase Order Setup');
		});
	}

	getPreview(copyNumber: number): void {
		this.service.getPurchaseOrderPreview(copyNumber, this.siteId).subscribe((data: string[]) => {
			let message = '';
			data.forEach((page, index) => {
				message += `<img alt="Purchase Order Preview Page - ${index + 1}" src="${page}" /><br />`;
			});
			this.dialog.open(ModalComponent, {
				data: {
					title: 'Purchase Order Preview',
					message,
					showCancel: false,
				},
			});
		});
	}

	private loadSites() {
		this.service.getSites().subscribe((data: PurchaseOrderSetupSiteModel[]) => {
			this.sites = data;
			if (this.sites.length > 0) {
				this.siteId = this.sites[0].Id;
			}
			this.loadSetup();
		});
	}

	private loadUserEmails() {
		this.service.getEmailsForActiveUsers().subscribe((data: WrappedUserInfoModel) => {
			this.userEmails = data.users.map((item) => item.Email).sort((a: string, b: string) => a?.localeCompare(b));
		});
	}

	private loadSetup() {
		this.service.getBySiteId(this.siteId).subscribe((data: PurchaseOrderSetupModel) => {
			this.entity = data;
			this.entity.NumberOfCopies = this.entity.NumberOfCopies < 3 ? this.entity.NumberOfCopies : 3;
			this.entity.SitePOSetupCopies = this.entity.SitePOSetupCopies || [];
			this.numberOfCopiesChanged();

			this.originalEntity = JSON.stringify(this.entity);

			if (this.filteredUserEmails === undefined) {
				nextTick(() => {
					this.filteredUserEmails = this.poSetupForm.controls.SourceEmailAddress.valueChanges.pipe(
						startWith(''),
						map((value) => this.userEmails.filter((option) => (option?.toLowerCase() || '').includes(value?.toLowerCase() || ''))),
					);
				});
			}
		});
	}
}
